import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cn } from "@/lib/utils";
import { Check } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { RadioGroup } from "@/components/ui/radio-group";

export interface SelectableCardOption<T extends string> {
  value: T;
  label: React.ReactNode;
  description?: React.ReactNode;
  /** 우측에 붙는 보조 정보 (사업자등록번호 등) */
  meta?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** 비활성 사유 배지 ("이미 등록됨") */
  disabledReason?: React.ReactNode;
}

interface SelectableCardGroupProps<T extends string> {
  options: SelectableCardOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  orientation?: "horizontal" | "vertical";
  className?: string;
}

/**
 * 카드형 단일 선택 그룹. `radio-group` 은 원형 라디오만 그리므로 카드 전체를 라디오로 쓴다.
 *
 * 선택하지 않은 카드는 아이콘·제목·설명이 모두 한 단계 흐려진다(시안) — 아이콘에 `color`
 * 를 넘기지 않아야 카드 상태를 따라 색이 바뀐다.
 *
 * 우측 슬롯은 `disabledReason` 배지 → `meta` → 선택 시 체크 아이콘 순으로 하나만 노출된다.
 * (2택 카드는 선택 표시에 체크 아이콘을, 검색 결과 카드는 보조 정보를 우측에 둔다)
 *
 * @example
 * ```tsx
 * <SelectableCardGroup
 *   orientation="horizontal"
 *   value={stationScope}
 *   onChange={setStationScope}
 *   options={[
 *     { value: "ALL", label: "전체 충전소 이용", description: "운영 중인 모든 충전소를 이용해요." },
 *     { value: "SELECTED", label: "특정 충전소만 이용", description: "선택한 일부 충전소만 이용해요." },
 *   ]}
 * />
 * ```
 */
export function SelectableCardGroup<T extends string>({
  options,
  value,
  onChange,
  orientation = "vertical",
  className,
}: SelectableCardGroupProps<T>) {
  const isHorizontal = orientation === "horizontal";

  return (
    <RadioGroup
      value={value ?? ""}
      onValueChange={(next) => onChange(next as T)}
      className={cn(
        "gap-2",
        // 가로 배치는 grid 로 둔다 — auto-cols-fr 은 minmax(0,1fr) 이라 카드 내용이
        // 길어져도 칸 폭이 정확히 같다 (flex-1 은 min-content 밑으로는 안 줄어든다)
        isHorizontal ? "grid grid-flow-col auto-cols-fr" : "flex flex-col",
        className,
      )}
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        const isDisabled = option.disabled === true;

        return (
          <RadioGroupPrimitive.Item
            key={option.value}
            value={option.value}
            disabled={isDisabled}
            className={cn(
              "relative flex gap-3 rounded-xl p-3 text-left transition-all outline-none",
              "items-center",
              "w-full min-w-0",
              isDisabled
                ? "bg-fills-neutral inset-border-solid-normal cursor-not-allowed"
                : cn(
                    "cursor-pointer",
                    // hover·포커스 테두리는 CSS 가 붙는다 (global.css 의 Card Border)
                    isSelected
                      ? "bg-primary-normal/[0.06] inset-border-card-selected"
                      : "bg-bg-primary inset-border-card",
                  ),
            )}
          >
            {/* 아이콘은 색을 지정하지 않으면 currentColor 라 카드 상태를 따라간다 */}
            {option.icon && (
              <span
                className={cn(
                  "shrink-0 flex items-center",
                  isDisabled
                    ? "text-labels-disable"
                    : isSelected
                      ? "text-primary-strong"
                      : "text-labels-alternative",
                )}
              >
                {option.icon}
              </span>
            )}

            <span className="flex flex-col min-w-0 flex-1">
              <span
                className={cn(
                  "typo-body-2-semibold",
                  isDisabled
                    ? "text-labels-disable"
                    : isSelected
                      ? "text-primary-strong"
                      : "text-labels-alternative",
                )}
              >
                {option.label}
              </span>
              {option.description && (
                <span
                  className={cn(
                    "typo-label-2-medium",
                    isDisabled
                      ? "text-labels-disable"
                      : isSelected
                        ? "text-primary-normal"
                        : "text-labels-assistive",
                  )}
                >
                  {option.description}
                </span>
              )}
            </span>

            {isDisabled && option.disabledReason ? (
              <Badge variant="none" size="md" className="shrink-0">
                {option.disabledReason}
              </Badge>
            ) : option.meta ? (
              <span
                className={cn(
                  "shrink-0 typo-caption-1-medium",
                  isDisabled ? "text-labels-disable" : "text-labels-assistive",
                )}
              >
                {option.meta}
              </span>
            ) : (
              /* 체크는 선택했을 때만 보이되 자리는 늘 지킨다 — 넣었다 뺐다 하면
                 선택할 때마다 본문 열 폭이 24px 흔들린다 */
              <span
                aria-hidden={!isSelected}
                className={cn(
                  "flex shrink-0 items-center",
                  !isSelected && "invisible",
                )}
              >
                <Check size={24} color="primary-strong" />
              </span>
            )}
          </RadioGroupPrimitive.Item>
        );
      })}
    </RadioGroup>
  );
}

SelectableCardGroup.displayName = "SelectableCardGroup";
